import React, { useState } from "react";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { MDSIconButton, MDSIconButtonSize } from "@/mds/components/buttons";
import {
  MDSText,
  MDSTextColor,
  MDSTextOverflow,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds/components/text";
import styled from "@emotion/styled";
import { motion } from "motion/react";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds";

interface MDSTabProps extends React.PropsWithChildren {
  iconKind?: MdsIconKind | React.ReactNode;
  isActive: boolean;
  isActivelyRecording?: boolean;
  isDragging?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onClickSplit?: (e: React.MouseEvent) => void;
  onClickClose?: (e: React.MouseEvent) => void;
}

export const MDSTab = React.forwardRef<HTMLDivElement, MDSTabProps>(
  (
    {
      iconKind,
      isActive,
      isActivelyRecording,
      isDragging,
      children,
      onClick,
      onClickSplit,
      onClickClose,
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = useState(false);
    const hasButtons = !!(onClickSplit || onClickClose);

    return (
      <StyledContainer
        ref={ref}
        $isActive={isActive}
        $isDragging={isDragging}
        $hasButtons={hasButtons}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {!!iconKind && (
          <StyledTabIconContainer>
            {typeof iconKind === "string" ? <MdsIcon kind={iconKind as MdsIconKind} /> : iconKind}
          </StyledTabIconContainer>
        )}

        <StyledTabLabel
          variant={MDSTextVariant.TextXs}
          weight={MDSTextWeight.Medium}
          numberOfLines={1}
          overflow={MDSTextOverflow.Ellipsis}
          color={isActivelyRecording ? MDSTextColor.Emphasis : MDSTextColor.Primary}
        >
          {children}
        </StyledTabLabel>
        {hasButtons && (
          <StyledButtonGroup
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            $isHovered={isHovered}
            $isActive={isActive}
          >
            {onClickSplit && (
              <MDSTooltip
                config={{
                  label: "Split pane with tab",
                  placement: MDSTooltipPlacement.Bottom,
                }}
              >
                <MDSIconButton
                  size={MDSIconButtonSize.Small}
                  iconKind={MdsIconKind.SplitPanel}
                  onClick={onClickSplit}
                />
              </MDSTooltip>
            )}
            {onClickClose && (
              <MDSTooltip
                config={{
                  label: "Close tab",
                  placement: MDSTooltipPlacement.Bottom,
                }}
              >
                <MDSIconButton
                  size={MDSIconButtonSize.Small}
                  iconKind={MdsIconKind.Exit}
                  onClick={onClickClose}
                />
              </MDSTooltip>
            )}
          </StyledButtonGroup>
        )}
      </StyledContainer>
    );
  }
);

MDSTab.displayName = "MDSTab";

export const StyledContainer = styled.div<{
  $isActive: boolean;
  $isDragging?: boolean;
  $hasButtons?: boolean;
}>`
  display: flex;
  align-items: center;
  position: relative;

  height: 40px;
  width: 200px;
  padding: ${({ theme }) => theme.spacing["0.5"]};
  padding-inline: ${({ theme }) => theme.spacing["3"]};

  background-color: ${({ theme, $isActive }) =>
    $isActive
      ? theme.colors.background.button.tabBar.active
      : theme.colors.background.button.tabBar.default};
  box-shadow: ${({ theme, $isDragging }) => ($isDragging ? theme.shadows.surface : "none")};
  border-bottom: 1px solid
    ${({ theme, $isActive }) => ($isActive ? theme.colors.background.activeTab : "transparent")};
  border-right: 1px solid ${({ theme }) => theme.colors.border.chrome};
  cursor: pointer;

  transition: all 0.2s ease;
  &:hover {
    background-color: ${({ theme, $isActive }) =>
      $isActive
        ? theme.colors.background.button.tabBar.active
        : theme.colors.background.button.tabBar.hover};
  }
`;

const StyledTabLabel = styled(MDSText)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 0;
  min-width: 0;
`;

const StyledButtonGroup = styled(motion.div)<{ $isHovered: boolean; $isActive: boolean }>`
  display: flex;
  align-items: center;
  overflow: hidden;
  flex-shrink: 0;
  position: absolute;
  right: ${({ theme }) => theme.spacing["0.5"]};
  z-index: 1;
  transition: background-color 0.5s ease;
  background-color: ${({ theme, $isHovered, $isActive }) =>
    $isActive
      ? theme.colors.background.button.tabBar.active
      : $isHovered
        ? theme.colors.background.button.tabBar.hover
        : "transparent"};
`;

const StyledTabIconContainer = styled.div`
  padding-right: ${({ theme }) => theme.spacing["1"]};
  .mds-icon {
    font-size: 10px;
    height: 10px;
    width: 10px;
  }
`;
